import { collection, addDoc } from 'firebase/firestore';
import { db } from './firebase';

// Where lead notifications go. Firestore rules only allow `mail` docs addressed here.
export const LEAD_NOTIFY_EMAIL = 'matt@dudigital.ca';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const row = (label, value) => `
  <tr>
    <td style="padding:6px 12px 6px 0;color:#64748b;font-size:13px;vertical-align:top;white-space:nowrap">${label}</td>
    <td style="padding:6px 0;color:#0f172a;font-size:14px">${value}</td>
  </tr>`;

// Queues an email via the Firebase "Trigger Email" extension (watches the `mail` collection).
// Never throws: a failed notification must not break the applicant's submission.
export async function sendLeadEmail({ subject, heading, color, rows, notes }) {
  const html = `
  <div style="font-family:Arial,sans-serif;max-width:560px">
    <div style="background:${color};color:#fff;padding:14px 18px;border-radius:10px 10px 0 0;font-size:18px;font-weight:bold">
      ${heading}
    </div>
    <div style="border:1px solid #e2e8f0;border-top:none;padding:16px 18px;border-radius:0 0 10px 10px">
      <table style="border-collapse:collapse;width:100%">${rows.map(([l, v]) => row(l, v)).join('')}</table>
      ${notes ? `<div style="margin-top:14px;padding:12px;background:#f8fafc;border-radius:8px;font-size:14px;color:#0f172a">${notes}</div>` : ''}
    </div>
  </div>`;

  try {
    await addDoc(collection(db, 'mail'), {
      to: LEAD_NOTIFY_EMAIL,
      message: { subject, html },
    });
  } catch (err) {
    console.error('Lead notification failed to queue:', err);
  }
}

// ── Apply page ────────────────────────────────────────────────────
export function sendApplicationEmail({ name, email, phone, pages, description, disqSelected, featureLabels, qualified, pageLabel }) {
  const telHref = 'tel:' + phone.replace(/[^\d+]/g, '');
  const wanted = disqSelected.map(id => featureLabels[id] || id);

  // Why they didn't auto-qualify, and how to pitch them anyway
  const reasons = [];
  if ((parseInt(pages, 10) || 99) > 6) reasons.push(`Wants ${pageLabel} (free tier is up to 6)`);
  if (wanted.length) reasons.push(`Wants paid features: ${wanted.join(', ')}`);
  if (description.trim().length < 25) reasons.push('Very short project description');

  const pitch = qualified
    ? 'Approved for the free build. Call now, get them on hosting ($25 CAD/mo) and send the intake form.'
    : 'Still closable: offer the free base site + quote the extras, or trim scope to fit the free tier.';

  const subject = qualified
    ? `🔥 QUALIFIED lead: ${name} · ${phone}`
    : `⚠️ Unqualified lead (upsell): ${name} · ${phone}`;

  return sendLeadEmail({
    subject,
    heading: qualified ? '🔥 New QUALIFIED free-website lead' : '⚠️ New lead: needs paid extras',
    color: qualified ? '#059669' : '#d97706',
    rows: [
      ['Name', `<b>${esc(name)}</b>`],
      ['Phone', `<a href="${esc(telHref)}" style="font-size:16px;font-weight:bold">📞 ${esc(phone)}</a>`],
      ['Email', `<a href="mailto:${esc(email)}">${esc(email)}</a>`],
      ['Pages', esc(pageLabel)],
      ['Extras wanted', wanted.length ? esc(wanted.join(', ')) : 'None'],
      ['Project', esc(description)],
      ...(reasons.length ? [['Why not auto-qualified', esc(reasons.join(' · '))]] : []),
    ],
    notes: `<b>Next step:</b> ${pitch}`,
  });
}
